/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";

/** @module react/plugins/input-presets/password-preset */
import { Types } from '@codistica/types';
import { spaceBlocker } from '../../input-blockers/internals/space-blocker.js';
import { spaceFilter } from '../../input-filters/internals/space-filter.js';
import { passwordValidator } from '../../input-validators/internals/password-validator.js';
var passwordPresetSchema = new Types({
  options: {
    type: 'Object',
    def: {
      errorMessages: {
        type: 'Object',
        def: {
          generic: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          length: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          numbers: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          },
          lowercases: {
            type: ['string', 'Function', 'null'],
            def: null
          },
          uppercases: {
            type: ['string', 'Function', 'null'],
            def: null
          },
          specials: {
            type: ['string', 'Function', 'Object', 'null'],
            def: null
          }
        }
      }
    }
  }
});
/**
 * @typedef passwordPresetErrorMessagesType
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [generic=null] - Generic error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [length=null] - Wrong length error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [numbers=null] - Minimum required numbers error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [lowercases=null] - Minimum required lowercase characters error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [uppercases=null] - Minimum required uppercase characters error message.
 * @property {(string|(function(*): string|null)|Object<string,*>|null)} [specials=null] - Minimum required special characters error message.
 */

/**
 * @typedef passwordPresetOptionsType
 * @property {passwordPresetErrorMessagesType} [errorMessages] - Validation error messages.
 */

/**
 * @description Password preset.
 * @param {passwordPresetOptionsType} [options] - Preset options.
 * @returns {{type: 'preset', name: string, groupErrorMessages: Array<string>, plugin: Array<*>}} Preset.
 */

function passwordPreset(options) {
  var _passwordPresetSchema = passwordPresetSchema.validate({
    options: options
  });

  options = _passwordPresetSchema.options;

  var _options$errorMessage = options.errorMessages,
      generic = _options$errorMessage.generic,
      otherErrorMessages = _objectWithoutProperties(_options$errorMessage, ["generic"]);

  return {
    type: 'preset',
    name: 'passwordPreset',
    groupErrorMessages: {
      generic: generic
    },
    plugin: [spaceBlocker, spaceFilter, passwordValidator({
      minLength: 8,
      maxLength: 30,
      errorMessages: otherErrorMessages
    })]
  };
}

export { passwordPreset };